import { SkeletonPlaceholder } from '@danielsledz/react-native-skeleton-placeholder/dist/index.esm';
import React, { memo, useCallback, useState } from 'react';

import { Stack } from '@/components/layout';
import { colors } from '@/theme/colors';
import type { DimensionValue, LayoutChangeEvent, LayoutRectangle, ViewProps } from 'react-native';

export interface SkeletonProps extends ViewProps {
  colorMode?: 'dark' | 'light';
  width?: DimensionValue;
  height?: DimensionValue;
  borderRadius?: number;
  backgroundColor?: string;
  highlightColor?: string;
}

export type SkeletonGroupProps = Omit<React.ComponentProps<typeof Stack>, 'viewType' | 'colors'> & {
  viewType?: 'view';
};

export type SkeletonAvatarProps = Omit<SkeletonProps, 'width' | 'height' | 'borderRadius'> & {
  size?: number;
};

export type SkeletonLineProps = Omit<SkeletonProps, 'height'> & {
  height?: number;
};

export type SkeletonPlayerCardProps = Omit<SkeletonGroupProps, 'children'> &
  Pick<SkeletonProps, 'colorMode' | 'backgroundColor' | 'highlightColor'> & {
    avatarSize?: number;
    lineGap?: number;
  };

export type SkeletonListPlayerHeaderProps = Omit<SkeletonGroupProps, 'children'> &
  Pick<SkeletonProps, 'colorMode' | 'backgroundColor' | 'highlightColor'> & {
    isTablet?: boolean;
  };

const SkeletonRoot = (props: SkeletonProps) => {
  const {
    colorMode = 'light',
    width,
    height,
    borderRadius = 6,
    backgroundColor,
    highlightColor,
    ...rest
  } = props;
  const [layout, setLayout] = useState<LayoutRectangle | null>(null);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setLayout(event.nativeEvent.layout);
  }, []);

  const fallbackBackgroundColor = colorMode === 'dark' ? colors['6'] : colors['1'];
  const fallbackHighlightColor = colorMode === 'dark' ? colors['7'] : colors['3'];

  return (
    <Stack
      {...rest}
      onLayout={onLayout}
      style={[{ borderRadius, width: width || '100%', height: height || '100%' }, rest.style]}>
      <SkeletonPlaceholder
        speed={2000}
        enabled={true}
        shimmerWidth={(layout?.width ?? 0) * 2}
        backgroundColor={backgroundColor ?? fallbackBackgroundColor}
        highlightColor={highlightColor ?? fallbackHighlightColor}>
        <SkeletonPlaceholder.Item width="100%" height="100%" />
      </SkeletonPlaceholder>
    </Stack>
  );
};

const SkeletonGroup = memo(
  ({ children, direction = 'column', gap = 8, ...rest }: SkeletonGroupProps) => {
    return (
      <Stack direction={direction} gap={gap} {...rest}>
        {children}
      </Stack>
    );
  },
);

const SkeletonAvatar = memo(({ size = 40, ...rest }: SkeletonAvatarProps) => {
  return <SkeletonRoot width={size} height={size} borderRadius={size / 2} {...rest} />;
});

const SkeletonLine = memo(({ height = 12, borderRadius = 6, ...rest }: SkeletonLineProps) => {
  return <SkeletonRoot height={height} borderRadius={borderRadius} {...rest} />;
});

const SkeletonPlayerCard = memo(
  ({
    avatarSize = 48,
    lineGap = 8,
    colorMode,
    backgroundColor,
    highlightColor,
    ...rest
  }: SkeletonPlayerCardProps) => {
    return (
      <SkeletonGroup
        direction="row"
        align="center"
        gap={12}
        width="100%"
        paddingX={16}
        paddingY={12}
        borderRadius={8}
        backgroundColor={colors['0']}
        {...rest}>
        <SkeletonAvatar
          size={avatarSize}
          colorMode={colorMode}
          backgroundColor={backgroundColor}
          highlightColor={highlightColor}
        />
        <SkeletonGroup flex={1} gap={lineGap}>
          <SkeletonLine
            width="60%"
            height={14}
            colorMode={colorMode}
            backgroundColor={backgroundColor}
            highlightColor={highlightColor}
          />
          <SkeletonLine
            width="35%"
            height={12}
            colorMode={colorMode}
            backgroundColor={backgroundColor}
            highlightColor={highlightColor}
          />
        </SkeletonGroup>
      </SkeletonGroup>
    );
  },
);

const SkeletonListPlayerHeader = memo(
  ({
    isTablet = false,
    colorMode,
    backgroundColor,
    highlightColor,
    ...rest
  }: SkeletonListPlayerHeaderProps) => {
    if (isTablet) {
      return (
        <SkeletonGroup gap={12} width="100%" {...rest}>
          <SkeletonGroup direction="row" align="center" gap={12}>
            <SkeletonLine
              width={120}
              height={36}
              borderRadius={18}
              colorMode={colorMode}
              backgroundColor={backgroundColor}
              highlightColor={highlightColor}
            />
            <SkeletonLine
              width={120}
              height={36}
              borderRadius={18}
              colorMode={colorMode}
              backgroundColor={backgroundColor}
              highlightColor={highlightColor}
            />
            <SkeletonGroup flex={1}>
              <SkeletonLine
                width="100%"
                height={40}
                borderRadius={20}
                colorMode={colorMode}
                backgroundColor={backgroundColor}
                highlightColor={highlightColor}
              />
            </SkeletonGroup>
            <SkeletonLine
              width={110}
              height={40}
              borderRadius={20}
              colorMode={colorMode}
              backgroundColor={backgroundColor}
              highlightColor={highlightColor}
            />
          </SkeletonGroup>
          <SkeletonGroup direction="row" align="center" justify="space-between">
            <SkeletonLine
              width={140}
              height={14}
              colorMode={colorMode}
              backgroundColor={backgroundColor}
              highlightColor={highlightColor}
            />
            <SkeletonLine
              width={160}
              height={26}
              borderRadius={13}
              colorMode={colorMode}
              backgroundColor={backgroundColor}
              highlightColor={highlightColor}
            />
            <SkeletonLine
              width={120}
              height={12}
              colorMode={colorMode}
              backgroundColor={backgroundColor}
              highlightColor={highlightColor}
            />
          </SkeletonGroup>
        </SkeletonGroup>
      );
    }

    return (
      <SkeletonGroup gap={8} width="100%" {...rest}>
        <SkeletonLine
          width="100%"
          height={40}
          borderRadius={20}
          colorMode={colorMode}
          backgroundColor={backgroundColor}
          highlightColor={highlightColor}
        />
        <SkeletonGroup direction="row" gap={8}>
          <SkeletonLine
            width={110}
            height={34}
            borderRadius={17}
            colorMode={colorMode}
            backgroundColor={backgroundColor}
            highlightColor={highlightColor}
          />
          <SkeletonLine
            width={110}
            height={34}
            borderRadius={17}
            colorMode={colorMode}
            backgroundColor={backgroundColor}
            highlightColor={highlightColor}
          />
        </SkeletonGroup>
        <SkeletonLine
          width={150}
          height={10}
          colorMode={colorMode}
          backgroundColor={backgroundColor}
          highlightColor={highlightColor}
        />
        <SkeletonLine
          width="100%"
          height={24}
          borderRadius={12}
          colorMode={colorMode}
          backgroundColor={backgroundColor}
          highlightColor={highlightColor}
        />
      </SkeletonGroup>
    );
  },
);

export const Skeleton = Object.assign(SkeletonRoot, {
  Group: SkeletonGroup,
  Avatar: SkeletonAvatar,
  Line: SkeletonLine,
  PlayerCard: SkeletonPlayerCard,
  ListPlayerHeader: SkeletonListPlayerHeader,
});
